import { useEffect, useRef, type ReactNode } from "react";

interface CatalogInfiniteListProps {
  /** Number of items currently rendered — re-arms the observer after each load. */
  dataLength: number;
  hasMore: boolean;
  /** Advance to the next page. */
  next: () => void;
  gridClassName: string;
  children: ReactNode;
}

/**
 * Mobile-only auto-loading list for the catalog listing.
 * A sentinel below the grid is watched with an IntersectionObserver; as it nears
 * the viewport the next page is pulled in, with a "loading more" indicator. On
 * web/tablet the listing keeps the numbered pagination instead (see
 * CategoryListingPage).
 */
export default function CatalogInfiniteList({
  dataLength,
  hasMore,
  next,
  gridClassName,
  children,
}: CatalogInfiniteListProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !fired) {
          fired = true;
          nextRef.current();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // Re-arm whenever more items load (dataLength) or hasMore flips.
  }, [hasMore, dataLength]);

  return (
    <>
      <div className={gridClassName}>{children}</div>

      {hasMore ? (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center gap-3 py-8 text-[14px] font-medium text-landing-body"
        >
          <span
            className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--landing-button)] border-t-transparent"
            aria-hidden
          />
          Loading more classes…
        </div>
      ) : (
        <p className="py-8 text-center text-[13px] font-medium text-landing-muted">
          You&rsquo;ve reached the end of the list
        </p>
      )}
    </>
  );
}
